import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import posthog from "@/lib/posthog";
import "@/lib/linkedinInsight";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

import MarketingHomeV1 from "@/pages/MarketingHomeV1";
import WaitlistLanding from "@/pages/WaitlistLanding";
import LandingHomeFinal from "@/pages/LandingHomeFinal";
import LandingHomeV2 from "@/pages/LandingHomeV2";
import SiteLanding from "@/site/pages/SiteLanding";
import SiteAbout from "@/site/pages/SiteAbout";
import SiteAgents from "@/site/pages/SiteAgents";
import SiteSolutions from "@/site/pages/SiteSolutions";
import SitePricing from "@/site/pages/SitePricing";
import SiteSecurity from "@/site/pages/SiteSecurity";
import SiteThoughts from "@/site/pages/SiteThoughts";
import SitePrivacy from "@/site/pages/SitePrivacy";
import SiteTerms from "@/site/pages/SiteTerms";
import SiteBrandGuidelines from "@/site/pages/SiteBrandGuidelines";
import SiteChangelog from "@/site/pages/SiteChangelog";
import SiteGifting from "@/site/pages/SiteGifting";
import AuthCallback from "@/pages/AuthCallback";
import DesktopAuthCallback from "@/pages/DesktopAuthCallback";
import Dashboard from "@/pages/Dashboard";
import LiveMeetingV2 from "@/pages/LiveMeetingV2";
import MeetingReview from "@/pages/MeetingReview";
import Onboarding from "@/pages/Onboarding";
import DemoMeetingV1 from "@/pages/DemoMeetingV1";
import CardTestPage from "@/pages/CardTestPage";
import SourceLibrary from "@/pages/SourceLibrary";
import SettingsPage from "@/pages/SettingsPage";
import PipLauncher from "@/pages/PipLauncher";
import ConversationalIntelligencePage from "@/pages/ConversationalIntelligencePage";
import AgentMarginsPage from "@/pages/AgentMarginsPage";
import SidequestsPage from "@/pages/SidequestsPage";
import AgentControlLandingPage from "@/pages/AgentControlLandingPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import PlatformLandingPage from "@/pages/platform/PlatformLandingPage";
import ApiDocsPage from "@/pages/platform/ApiDocsPage";
import TravelRedirect from "@/pages/TravelRedirect";

// Auth context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(resp.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_token=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      posthog.identify(user.user_id, { email: user.email, name: user.name });
    }
  }, [user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { }
    posthog.reset();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

function PrepRedirect() {
  const { eventId } = useParams();
  return <Navigate to={`/meetings/${eventId}/live`} replace />;
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center">
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-body text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) return null;

  return children;
};

// Dedicated domain(s) for the Trigrly gifting landing page. On these hosts
// the root path serves <SiteGifting /> instead of the main Shadow landing, so
// trigrly.com/ shows the gifting page with a clean URL. Everything else in the
// SPA still resolves normally on both domains. shadowlabs.ai is unaffected.
const GIFTING_HOSTS = new Set(["trigrly.com", "www.trigrly.com"]);

function AppRouter() {
  const location = useLocation();

  const isGiftingDomain =
    typeof window !== "undefined" && GIFTING_HOSTS.has(window.location.hostname);

  useEffect(() => {
    if (isGiftingDomain || location.pathname === '/gifting') {
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = '/trigrly-favicon.svg';
    }
  }, [isGiftingDomain, location.pathname]);

  // Check URL fragment synchronously for session_id (prevents race conditions)
  if (location.hash?.includes('session_token=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={isGiftingDomain ? <SiteGifting /> : <SiteLanding />} />
      <Route path="/archive-1" element={<LandingHomeFinal />} />
      <Route path="/about" element={<SiteAbout />} />
      <Route path="/agents" element={<SiteAgents />} />
      <Route path="/solutions" element={<SiteSolutions />} />
      <Route path="/security" element={<SiteSecurity />} />
      <Route path="/thoughts" element={<SiteThoughts />} />
      <Route path="/brand-guidelines" element={<SiteBrandGuidelines />} />
      <Route path="/changelog" element={<SiteChangelog />} />
      <Route path="/gifting" element={<SiteGifting />} />
      <Route path="/vision" element={<Navigate to="/thoughts" replace />} />
      <Route path="/v2" element={<LandingHomeV2 />} />
      <Route path="/waitlist" element={<WaitlistLanding />} />
      <Route path="/trynow" element={<MarketingHomeV1 />} />
      <Route path="/auth/desktop-callback" element={<DesktopAuthCallback />} />
      <Route path="/privacy" element={<SitePrivacy />} />
      <Route path="/terms" element={<SiteTerms />} />
      <Route path="/demo" element={<DemoMeetingV1 />} />
      <Route path="/agent-control" element={<AgentControlLandingPage />} />
      <Route path="/conversational-intelligence" element={<ConversationalIntelligencePage />} />
      <Route path="/agent-margins" element={<AgentMarginsPage />} />
      <Route path="/sidequests" element={<SidequestsPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/cards-test" element={<ProtectedRoute><CardTestPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/sources" element={<ProtectedRoute><SourceLibrary /></ProtectedRoute>} />
      <Route path="/pricing" element={<SitePricing />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/travel" element={<TravelRedirect />} />
      <Route path="/platform" element={<PlatformLandingPage />} />
      <Route path="/platform/docs" element={<ApiDocsPage />} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/meetings/:eventId/prep" element={<PrepRedirect />} />
      <Route path="/meetings/:eventId/live" element={<ProtectedRoute><LiveMeetingV2 /></ProtectedRoute>} />
      <Route path="/meetings/:eventId/review" element={<ProtectedRoute><MeetingReview /></ProtectedRoute>} />
      <Route path="/pip/:eventId" element={<ProtectedRoute><PipLauncher /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen">
          <AppRouter />
        </div>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
